import dynamic from 'next/dynamic';

import type { PartnerBlockSectionProps } from 'src/components/sections/PartnerBlockSection/PartnerBlockSection';
import { PartnerBlockImages } from 'src/featureFlags/featureFlags';
import { fetchUnifiedProducts } from 'src/services/unified-product-service/fetchUnifiedProducts';
import type { SectionRenderer } from 'src/types/Section';
import type { AsyncTransformer } from 'src/types/Transformer';
import { lazyLoadImages } from 'src/utils/images';
import { contentPartnerToModel } from './contentToModel';
import type { ContentPartner, PartnerBlockContentSection } from './types';
import { getProductsValidationErrors } from './validation';

/* istanbul ignore next */
export const renderer: SectionRenderer<PartnerBlockSectionProps> =
  dynamic<PartnerBlockSectionProps>(() =>
    import('src/components/sections/PartnerBlockSection/PartnerBlockSection').then(
      (mod) => mod.PartnerBlockSection,
    ),
  );

const getAllProductCodesFromPartners = (partners: ContentPartner[]) =>
  partners
    .reduce((prev, curr) => prev.concat(curr.products as never), [])
    .map(({ product_code }) => Number(product_code));

export const transformer: AsyncTransformer<
  PartnerBlockContentSection,
  PartnerBlockSectionProps
> = async (
  { _metadata, copy, heading, partners, tracking_title },
  { pageConfig, pageMetadata, positionMetadata },
) => {
  const productCodes = getAllProductCodesFromPartners(partners);
  const result = await fetchUnifiedProducts(productCodes);
  const renderableProducts = (result?.products ?? []).filter((product) => product.purchasable);
  const errors = getProductsValidationErrors(productCodes, result, renderableProducts);

  return {
    uid: _metadata.uid,
    props: {
      heading,
      copy,
      partners: partners.map((partner) => contentPartnerToModel(partner, renderableProducts)),
      trackingTitle: tracking_title,
      pageType: pageConfig.pageType,
      lazyImages: lazyLoadImages(positionMetadata.position),
      /* Opt in, unlike the PLP's own reading of its experiment, which treats anything but
         `single` as the gallery. Only an explicit `multiple` turns it on here, so a visitor
         the flag service says nothing about sees the hero alone. */
      showMultipleImages:
        pageMetadata.featureFlags[PartnerBlockImages.NAME] === PartnerBlockImages.MULTIPLE,
    },
    errors,
  };
};
